import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Settings,
  Shield,
  Bell,
  Palette,
  Save,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Phone,
  UserCircle,
  CheckCircle2,
  AlertCircle,
  Info
} from "lucide-react";
import { Button } from "../ui/Button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/Card";
import { Input } from "../ui/Input";
import { Label } from "../ui/Label";
import { Switch } from "../ui/Switch";
import { Separator } from "../ui/Separator";
import axios from "../axios/AxiosInstance";
import { message } from "antd";

export default function MonComptePage() {
  // State management
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [darkModeEnabled, setDarkModeEnabled] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [mail, setMail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  const updateProfile = async () => {
    setIsLoading(true);
    try {
      await axios.post("auth/update-profile", {
        firstName,
        lastName,
        mail,
        phone
      });
      message.success("Profil mis à jour avec succès !");
    } catch (error) {
      message.error("Erreur lors de la mise à jour du profil");
    } finally {
      setIsLoading(false);
    }
  };

  const updatePassword = async () => {
    if (newPassword !== confirmPassword) {
      message.error("Les mots de passe ne correspondent pas");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post("auth/update-password", { password, newPassword });
      message.success("Mot de passe modifié avec succès !");
      setPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      message.error("Erreur lors de la modification du mot de passe");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const firstName = localStorage.getItem("firstname");
    setFirstName(firstName);
    const lastName = localStorage.getItem("lastname");
    setLastName(lastName);
    const mail = localStorage.getItem("userEmail");
    setMail(mail);
    const phone = localStorage.getItem("phone");
    setPhone(phone);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-8 mb-8"
        >
          <div className="flex items-center space-x-4">
            <div className="bg-blue-100 p-4 rounded-xl">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Mon Compte</h1>
              <p className="text-slate-600 mt-1">Gérez vos informations personnelles et préférences</p>
            </div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 mb-8"
        >
          <div className="flex border-b border-slate-200">
            {[
              { id: "profile", label: "Profil", icon: User },
              { id: "security", label: "Sécurité", icon: Shield },
              { id: "preferences", label: "Préférences", icon: Settings },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <motion.button
                  key={tab.id}
                  whileHover={{ y: -2 }}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-4 text-sm font-medium border-b-2 transition-all duration-200 ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600 bg-blue-50"
                      : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === "profile" && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-8"
            >
              {/* Personal Information */}
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-slate-200">
                  <div className="flex items-center space-x-2">
                    <UserCircle className="w-6 h-6 text-blue-600" />
                    <CardTitle className="text-xl text-slate-800">
                      Informations personnelles
                    </CardTitle>
                  </div>
                  <CardDescription className="text-slate-600">
                    Gérez vos informations de profil
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-slate-700 font-medium flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        Prénom
                      </Label>
                      <Input
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Votre prénom"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-slate-700 font-medium flex items-center">
                        <User className="w-4 h-4 mr-2" />
                        Nom
                      </Label>
                      <Input
                        id="lastName"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Votre nom"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-slate-700 font-medium flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      value={mail}
                      onChange={(e) => setMail(e.target.value)}
                      className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="votre@email.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-slate-700 font-medium flex items-center">
                      <Phone className="w-4 h-4 mr-2" />
                      Téléphone
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={updateProfile}
                      disabled={isLoading}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Sauvegarder les informations
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>

              {/* Account Status */}
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="bg-gradient-to-r from-green-50 to-green-100 border-b border-slate-200">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <CardTitle className="text-xl text-slate-800">
                      Statut du compte
                    </CardTitle>
                  </div>
                  <CardDescription className="text-slate-600">
                    Informations sur votre compte
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                      <span className="text-slate-700 font-medium">Compte vérifié</span>
                    </div>
                    <span className="text-green-600 text-sm font-medium">Actif</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Mail className="w-5 h-5 text-blue-600" />
                      <span className="text-slate-700 font-medium">Email confirmé</span>
                    </div>
                    <span className="text-blue-600 text-sm font-medium">Vérifié</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Shield className="w-5 h-5 text-orange-600" />
                      <span className="text-slate-700 font-medium">Authentification 2FA</span>
                    </div>
                    <span className="text-orange-600 text-sm font-medium">Désactivée</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "security" && (
            <motion.div
              key="security"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="bg-gradient-to-r from-red-50 to-red-100 border-b border-slate-200">
                  <div className="flex items-center space-x-2">
                    <Shield className="w-6 h-6 text-red-600" />
                    <CardTitle className="text-xl text-slate-800">
                      Sécurité du compte
                    </CardTitle>
                  </div>
                  <CardDescription className="text-slate-600">
                    Modifiez votre mot de passe et gérez la sécurité
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword" className="text-slate-700 font-medium flex items-center">
                      <Lock className="w-4 h-4 mr-2" />
                      Mot de passe actuel
                    </Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500 pr-10"
                        placeholder="Votre mot de passe actuel"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword" className="text-slate-700 font-medium flex items-center">
                      <Lock className="w-4 h-4 mr-2" />
                      Nouveau mot de passe
                    </Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showNewPassword ? "text" : "password"}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500 pr-10"
                        placeholder="Votre nouveau mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-slate-700 font-medium flex items-center">
                      <Lock className="w-4 h-4 mr-2" />
                      Confirmer le nouveau mot de passe
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="border-slate-300 focus:border-blue-500 focus:ring-blue-500 pr-10"
                        placeholder="Confirmez votre nouveau mot de passe"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {newPassword && confirmPassword && newPassword !== confirmPassword && (
                    <div className="flex items-center space-x-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>Les mots de passe ne correspondent pas</span>
                    </div>
                  )}

                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={updatePassword}
                      disabled={isLoading || !password || !newPassword || newPassword !== confirmPassword}
                      className="w-full bg-red-600 hover:bg-red-700 text-white"
                    >
                      {isLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                      ) : (
                        <Lock className="w-4 h-4 mr-2" />
                      )}
                      Modifier le mot de passe
                    </Button>
                  </motion.div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {activeTab === "preferences" && (
            <motion.div
              key="preferences"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card className="bg-white border border-slate-200 shadow-sm">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 border-b border-slate-200">
                  <div className="flex items-center space-x-2">
                    <Settings className="w-6 h-6 text-purple-600" />
                    <CardTitle className="text-xl text-slate-800">
                      Préférences
                    </CardTitle>
                  </div>
                  <CardDescription className="text-slate-600">
                    Personnalisez votre expérience utilisateur
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Bell className="w-5 h-5 text-slate-600" />
                      <div>
                        <span className="text-slate-700 font-medium block">Notifications</span>
                        <span className="text-slate-500 text-sm">Recevoir des notifications par email</span>
                      </div>
                    </div>
                    <Switch
                      checked={notificationsEnabled}
                      onCheckedChange={setNotificationsEnabled}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Palette className="w-5 h-5 text-slate-600" />
                      <div>
                        <span className="text-slate-700 font-medium block">Mode sombre</span>
                        <span className="text-slate-500 text-sm">Activer le thème sombre</span>
                      </div>
                    </div>
                    <Switch
                      checked={darkModeEnabled}
                      onCheckedChange={setDarkModeEnabled}
                    />
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <span className="text-blue-800 font-medium block">Information</span>
                        <span className="text-blue-700 text-sm">
                          Les modifications de préférences sont sauvegardées automatiquement.
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
